/**
 * @fileOverview This file contains the implementation of the Create Item Modal component for Inventory module
 * @version 1.0.0
 * @since 1.0.0
 * @author Diego Benítez Reyna
 */

import React, { useEffect, useState } from 'react';
import Modal from 'react-bootstrap/Modal';
import { AdapterMoment } from '@mui/x-date-pickers-pro/AdapterMoment';
import moment from 'moment';
import customAxios from '../../utils/customAxios';
import { getToast } from '../../utils/toastConfig';
import { GlobalVariables } from '../../globalVar';
import { LocalizationProvider } from '@mui/x-date-pickers-pro';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import {
  Autocomplete,
  Checkbox,
  Divider,
  FormControl,
  FormHelperText,
  Grid,
  InputAdornment,
  TextField,
  createFilterOptions,
  Button,
} from '@mui/material';
import '../../scss/Components/_modal.scss';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogActions from '@mui/material/DialogActions';
import CurrencyInput from '../UI/CurrencyInput';
import ImageUploadInput from '../UI/ImageUploadInput';
import { isValueInArray, trimValue } from '../../utils/generalUtils';

const filter = createFilterOptions();

const CreateItemModal = ({ show, onHide, refreshData }) => {
  const initialCurrency = {
    id: 1,
    currency_name: 'Peso MXN',
    currency_code: 'MXN',
    currency_symbol: '$',
  }; // Default Peso MXN

  const [isLoading, setIsLoading] = useState(false);
  const [code, setCode] = useState('');
  const [name, setName] = useState('');
  const [model, setModel] = useState('');
  const [manufacturerId, setManufacturerId] = useState(null);
  const [equipmentIds, setEquipmentIds] = useState([]);
  const [locationId, setLocationId] = useState('');
  const [priority, setPriority] = useState(0);
  const [unit, setUnit] = useState('');
  const [unitId, setUnitId] = useState('');
  const [currency, setCurrency] = useState(initialCurrency); // Default Peso MXN
  const [currencyId, setCurrencyId] = useState(initialCurrency.id); // Default Peso MXN
  const [cost, setCost] = useState('');
  const [inventoryTypeId, setInventoryTypeId] = useState('');
  const [maxStock, setMaxStock] = useState(null);
  const [minStock, setMinStock] = useState(null);
  const [initialStock, setInitialStock] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);

  const [error, setError] = useState(false);

  const [manufacturerList, setManufacturerList] = useState([]);
  const [equipmentList, setEquipmentList] = useState([]);
  const [unitList, setUnitList] = useState([]);
  const [currencyList, setCurrencyList] = useState([]);
  const [inventoryTypeList, setInventoryTypeList] = useState([]);
  const [locationList, setLocationList] = useState([]);

  //#region LOCATION AUTOCOMPLETE
  const [open, setOpen] = useState(false);
  const initialDialogValue = useState({
    id: '',
    inventory_type_id: '',
    rack: '',
  });
  const [dialogValue, setDialogValue] = useState(initialDialogValue);
  const [location, setLocation] = useState(null);

  const handleCloseDialog = () => {
    setDialogValue(initialDialogValue);
    setOpen(false);
  };

  const handleSubmitDialog = async (e) => {
    e.preventDefault();
    try {
      const createLocationRes = await customAxios.post(
        `${GlobalVariables.getURL()}locations/create`,
        {
          inventory_type_id: inventoryTypeId,
          rack: dialogValue.rack,
        },
        {
          headers: {
            Authorization: 'bearer ' + localStorage.getItem('token'),
          },
        }
      );

      setLocationId(createLocationRes.data.insertId);

      setLocation({
        id: createLocationRes.data.insertId,
        inventory_type_id: inventoryTypeId,
        rack: dialogValue.rack,
      });

      await getLocationsList(inventoryTypeId);
    } catch (error) {
      console.log(error);
      getToast({ status: 'error', title: error.response.data.message });
      setLocation(null);
      setLocationId('');
    }

    handleCloseDialog();
  };

  //#endregion

  // Callbacks to handle data change in the currency component
  const cb = {
    handleCurrencyChange: (currencyData) => {
      setCurrencyId(currencyData.id);
      setCurrency(currencyData);
    },

    handleCostChange: (cost) => setCost(cost),
  };
  //#endregion

  const handleSubmit = async (e) => {
    setIsLoading(true);
    e.preventDefault();

    // Handle error when there are no equipments selected
    if (equipmentIds.length === 0) {
      setError(true);
      setIsLoading(false);
    } else {
      try {
        // Create formData object to send to backend
        const formData = new FormData();
        formData.append('code', code);
        formData.append('name', name);
        formData.append('model', model);
        formData.append('currency_id', currencyId);
        formData.append('cost', cost);
        formData.append('manufacturer_id', manufacturerId);
        formData.append('equipments', JSON.stringify(equipmentIds));
        formData.append('inventory_type_id', inventoryTypeId);
        formData.append('location_id', locationId);
        formData.append('priority', priority);
        formData.append('unit_id', unitId);
        formData.append('max_stock', maxStock);
        formData.append('min_stock', minStock);
        formData.append('initial_stock', initialStock);
        formData.append('image', selectedFile);

        const createRes = await customAxios.post(
          `${GlobalVariables.getURL()}inventory/createItem`,
          formData,
          {
            headers: {
              Authorization: 'bearer ' + localStorage.getItem('token'),
            },
          }
        );

        if (createRes.data.status === 'success') {
          getToast({
            status: 'success',
            title: 'Refacción creada correctamente',
          });
          resetFormFields();
          refreshData();
          onHide();
        }

        setIsLoading(false);
      } catch (error) {
        console.log(error);
        setIsLoading(false);
        getToast({ status: 'error', title: error.response.data.message });
      }
    }
  };

  const priorityList = [
    { id: 1, value: 0, name: 'Prioridad 0' },
    { id: 2, value: 1, name: 'Prioridad 1' },
    { id: 3, value: 2, name: 'Prioridad 2' },
  ];

  const handleClose = () => {
    resetFormFields();
    refreshData();
    onHide();
  };

  const resetFormFields = () => {
    setError(false);
    setCurrency(initialCurrency);
    setCurrencyId(initialCurrency.id);
    setInventoryTypeId('');
    setCost('');
    setCode('');
    setName('');
    setModel('');
    setManufacturerId(null);
    setEquipmentIds([]);
    setLocationId('');
    setLocation(null);
    setPriority(0);
    setUnit('');
    setUnitId('');
    setMaxStock(null);
    setMinStock(null);
    setInitialStock(null);
    setSelectedFile(null);

    setLocationList([]);
  };

  //#region Endpoint calls
  const getManufacturerList = async () => {
    try {
      const manufacturerRes = await customAxios.get(
        `${GlobalVariables.getURL()}manufacturers`,
        {
          headers: {
            Authorization: 'bearer ' + localStorage.getItem('token'),
          },
        }
      );

      setManufacturerList(manufacturerRes.data.data);
    } catch (error) {
      console.log(error);
      setManufacturerList([]);
    }
  };

  const getEquipmentList = async () => {
    try {
      const equipmentRes = await customAxios.get(
        `${GlobalVariables.getURL()}listEquipments`,
        {
          headers: {
            Authorization: 'bearer ' + localStorage.getItem('token'),
          },
        }
      );

      setEquipmentList(equipmentRes.data);
    } catch (error) {
      console.log(error);
      setEquipmentList([]);
    }
  };

  const getInventoryTypeList = async () => {
    try {
      const { data: res } = await customAxios.get(
        `${GlobalVariables.getURL()}inventory/getInventoryTypesList`,
        {
          headers: {
            Authorization: 'bearer ' + localStorage.getItem('token'),
          },
        }
      );
      setInventoryTypeList(res.data);
    } catch (error) {
      console.log(error);
      setInventoryTypeList([]);
    }
  };

  const getCurrencyAndUnitsList = async () => {
    try {
      const { data } = await customAxios.get(
        `${GlobalVariables.getURL()}autofillCurrenciesAndUnits`,
        {
          headers: {
            Authorization: 'bearer ' + localStorage.getItem('token'),
          },
        }
      );

      setUnitList(data.units);
      setCurrencyList(data.currencies);
    } catch (error) {
      console.log(error);
      setUnitList([]);
      setManufacturerList([]);
    }
  };

  const getLocationsList = async (inventoryTypeId) => {
    try {
      const locations = await customAxios.get(
        `${GlobalVariables.getURL()}locations/getLocationsByInventoryType/${inventoryTypeId}`,
        {
          headers: {
            Authorization: 'bearer ' + localStorage.getItem('token'),
          },
        }
      );

      setLocationList(locations.data.data || []);
    } catch (error) {
      console.log(error);
      setLocationList([]);
    }
  };
  //#endregion

  useEffect(() => {
    if (!show) return; // Only get data when modal is open

    getManufacturerList();
    getEquipmentList();
    getInventoryTypeList();
    getCurrencyAndUnitsList();
  }, [show]);

  return (
    <Modal
      show={show}
      onHide={handleClose}
      size="xl"
      aria-labelledby="contained-modal-title-vcenter"
      backdropClassName="modal-backdrop"
      centered
    >
      <Modal.Header closeButton>
        <Modal.Title>Nueva Refacción</Modal.Title>
      </Modal.Header>
      <LocalizationProvider
        dateAdapter={AdapterMoment}
        adapterLocale={moment.locale()}
      >
        <Modal.Body>
          <form id="items-form" onSubmit={handleSubmit}>
            <Divider className="mui-dividers">
              {'Información de la refacción'}
            </Divider>

            <Grid container spacing={2} sx={{ marginTop: 2 }}>
              <Grid item xs={12} md={4}>
                <ImageUploadInput
                  onFileSelect={(file) => setSelectedFile(file)}
                />
              </Grid>
              <Grid item xs={12} md={8}>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={12}>
                    <TextField
                      sx={{ width: '100%' }}
                      label={'Código'}
                      type="text"
                      value={code}
                      onChange={(e) => setCode(e.target.value)}
                      required
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      sx={{ width: '100%' }}
                      label={'Nombre'}
                      type="text"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      required
                    />
                  </Grid>
                  <Grid item xs={12} md={12}>
                    <TextField
                      sx={{ width: '100%' }}
                      label={'Modelo'}
                      type="text"
                      value={model}
                      onChange={(e) => setModel(e.target.value)}
                      required
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Autocomplete
                      disablePortal
                      includeInputInList={false}
                      options={manufacturerList}
                      sx={{ width: '100%' }}
                      getOptionLabel={(manufacturerList) =>
                        manufacturerList.name
                      }
                      isOptionEqualToValue={(option, value) =>
                        option.id === value.id
                      }
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Seleccionar fabricante..."
                          inputProps={{
                            ...params.inputProps,
                          }}
                          required={true}
                        />
                      )}
                      onChange={(e, value) => {
                        setManufacturerId(value.id);
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} md={8}>
                    <FormControl error={error} fullWidth>
                      <Autocomplete
                        disablePortal
                        multiple
                        disableCloseOnSelect
                        includeInputInList={false}
                        options={equipmentList}
                        sx={{ width: '100%' }}
                        getOptionLabel={(equipmentList) =>
                          equipmentList.equip_name
                        }
                        isOptionEqualToValue={(option, value) =>
                          option.id === value.id
                        }
                        renderOption={(props, option, { selected }) => (
                          <li {...props}>
                            <Checkbox
                              icon={
                                <CheckBoxOutlineBlankIcon fontSize="small" />
                              }
                              checkedIcon={<CheckBoxIcon fontSize="small" />}
                              style={{ marginRight: 8 }}
                              checked={selected}
                            />
                            {option.equip_name}
                          </li>
                        )}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="Seleccionar tipo(s) de equipo..."
                            inputProps={{
                              ...params.inputProps,
                            }}
                          />
                        )}
                        onChange={(e, value) => {
                          setEquipmentIds(
                            value.map((equipment) => equipment.id)
                          );
                        }}
                      />
                      {error && (
                        <FormHelperText>
                          Debe de escoger al menos una opción
                        </FormHelperText>
                      )}
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Autocomplete
                      disablePortal
                      includeInputInList={false}
                      options={priorityList}
                      sx={{ width: '100%' }}
                      getOptionLabel={(priorityList) => priorityList.name}
                      isOptionEqualToValue={(option, value) =>
                        option.id === value.id
                      }
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Seleccionar prioridad..."
                          inputProps={{
                            ...params.inputProps,
                          }}
                          required={true}
                        />
                      )}
                      onChange={(e, value) => setPriority(value.value)}
                    />
                  </Grid>
                  <Grid item xs={12} md={8}>
                    <CurrencyInput
                      currencyList={currencyList}
                      selectedCurrency={currency}
                      cost={cost}
                      cb={cb}
                    />
                  </Grid>
                </Grid>
              </Grid>
            </Grid>

            <Divider className="mui-dividers" sx={{ marginTop: 2 }}>
              {'Información de Almacén'}
            </Divider>

            <Grid container spacing={2} sx={{ marginTop: 2 }}>
              <Grid item xs={12} md={6}>
                <Autocomplete
                  disablePortal
                  includeInputInList={false}
                  options={inventoryTypeList}
                  sx={{ width: '100%' }}
                  getOptionLabel={(inventoryTypeList) => inventoryTypeList.name}
                  isOptionEqualToValue={(option, value) =>
                    option.id === value.id
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Seleccionar almacén..."
                      inputProps={{
                        ...params.inputProps,
                      }}
                      required={true}
                    />
                  )}
                  onChange={async (e, value) => {
                    if (!value) {
                      setInventoryTypeId('');
                      setLocationId('');
                      setLocation(null);
                      setLocationList([]);
                    } else {
                      setInventoryTypeId(value.id);
                      await getLocationsList(value.id);
                      setLocationId('');
                      setLocation(null);
                    }
                  }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Autocomplete
                  value={location}
                  onChange={(e, newValue) => {
                    // string is when the user types the option and doesn't exist
                    if (typeof newValue === 'string') {
                      // timeout to avoid instant validation of the dialog's form
                      setTimeout(() => {
                        setOpen(true);
                        setDialogValue({
                          inventory_type_id: inventoryTypeId,
                          rack: trimValue(newValue),
                        });
                      });
                    } else if (newValue && newValue.inputValue) {
                      setOpen(true);
                      setDialogValue({
                        inventory_type_id: inventoryTypeId,
                        rack: trimValue(newValue.inputValue),
                      });
                    } else {
                      setLocationId(newValue?.id || '');
                      setLocation(newValue || null);
                    }
                  }}
                  filterOptions={(options, params) => {
                    const filtered = filter(options, params);
                    const trimedValue = trimValue(params.inputValue);

                    if (
                      params.inputValue !== '' &&
                      !isValueInArray(trimedValue, locationList, 'rack')
                    ) {
                      filtered.push({
                        inputValue: trimedValue,
                        rack: `Agregar ${trimedValue}`,
                      });
                    }
                    return filtered;
                  }}
                  id="free-solo-dialog-demo"
                  options={locationList}
                  getOptionLabel={(option) => {
                    if (typeof option === 'string') return option;
                    if (option.inputValue) return option.inputValue;
                    return option.rack;
                  }}
                  selectOnFocus
                  clearOnBlur
                  handleHomeEndKeys
                  renderOption={(props, option) => (
                    <li {...props}>{option.rack}</li>
                  )}
                  sx={{ width: '100%' }}
                  freeSolo
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Seleccionar ubicación..."
                      required
                    />
                  )}
                />
                <Dialog open={open} onClose={handleCloseDialog}>
                  <form>
                    <DialogTitle>Agregar nueva ubicación</DialogTitle>
                    <DialogContent>
                      <DialogContentText>
                        {`No se encontró la ubicación ${dialogValue.rack}. ¿Desea agregarla?`}
                      </DialogContentText>
                      <TextField
                        autoFocus
                        margin="dense"
                        id="rack"
                        value={dialogValue.rack}
                        onChange={(event) =>
                          setDialogValue({
                            ...dialogValue,
                            rack: event.target.value,
                          })
                        }
                        label="# de rack"
                        type="text"
                        variant="standard"
                      />
                    </DialogContent>
                    <DialogActions>
                      <Button onClick={handleCloseDialog}>Cancelar</Button>
                      <Button onClick={handleSubmitDialog}>Agregar</Button>
                    </DialogActions>
                  </form>
                </Dialog>
              </Grid>
            </Grid>

            <Grid container spacing={2} sx={{ marginTop: 2 }}>
              <Grid item xs={12} md={3}>
                <Autocomplete
                  disablePortal
                  includeInputInList={false}
                  options={unitList}
                  sx={{ width: '100%' }}
                  getOptionLabel={(unitList) => unitList.unit_name}
                  isOptionEqualToValue={(option, value) =>
                    option.id === value.id
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Unidad de medida..."
                      inputProps={{
                        ...params.inputProps,
                      }}
                      required={true}
                    />
                  )}
                  onChange={(e, value) => {
                    setUnit(value.unit_name);
                    setUnitId(value.id);
                  }}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  sx={{ width: '100%' }}
                  label={'Stock máximo'}
                  type="number"
                  value={maxStock}
                  onChange={(e) => setMaxStock(e.target.value)}
                  required
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="start">{unit}</InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  sx={{ width: '100%' }}
                  label={'Stock mínimo'}
                  type="number"
                  value={minStock}
                  onChange={(e) => setMinStock(e.target.value)}
                  required
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="start">{unit}</InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  sx={{ width: '100%' }}
                  label={'Stock inicial'}
                  type="number"
                  value={initialStock}
                  onChange={(e) => setInitialStock(e.target.value)}
                  required
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="start">{unit}</InputAdornment>
                    ),
                  }}
                />
              </Grid>
            </Grid>
          </form>
        </Modal.Body>
      </LocalizationProvider>
      <Modal.Footer>
        <button
          type="submit"
          form="items-form"
          className="save-modal-button"
          disabled={isLoading}
        >
          Guardar
        </button>
      </Modal.Footer>
    </Modal>
  );
};

export default CreateItemModal;
